import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { requestPasswordGate } from "../component/layout/PasswordGateway";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateToTop } from "../utils/Constants";
import { API_BASE } from "../config/apiBase";
import Accordion from "../component/template/Accordion";
import useRecordings from "../hooks/useRecordings";
import useApiProfile from "../hooks/useApiProfile";
import Loading from "./Loading";
import { profileNeedsDashboardTour } from "../utils/onboarding";
import "../styles/pages/_home.scss";

const DASHBOARD_TOUR_PENDING_KEY = "mooz:dashboardTourPending";
const dashboardTourSeenKey = (userId) => `mooz:dashboardTourSeen:v1:${userId}`;

// ─── Stat helper ──────────────────────────────────────────────────────────────
const computeAvgScore = (recordings) => {
    const scores = recordings
        .map((r) => r?.feedback_score ?? r?.feedback?.score ?? r?.score ?? null)
        .map((v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        })
        .filter((v) => v !== null);
    if (!scores.length) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
};

const formatScore = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    const truncated = Math.trunc(n * 10) / 10;
    return truncated.toFixed(1);
};

const STEPS = [
    {
        n: "01",
        title: "Pick a question",
        body: "Browse real prompts sorted by role, difficulty, and interview stage.",
    },
    {
        n: "02",
        title: "Go on camera",
        body: "Practice with a countdown timer and optional prep notes. No distractions.",
    },
    {
        n: "03",
        title: "Replay and refine",
        body: "Watch yourself back, spot weak spots, and record a sharper take.",
    },
];

const readLocalStats = () => {
    try {
        const recordings = JSON.parse(
            localStorage.getItem("recordings") || "[]"
        );
        const answers = JSON.parse(localStorage.getItem("answers") || "{}");
        return {
            recordings,
            drafts: Object.keys(answers).length,
            avgScore: computeAvgScore(recordings),
        };
    } catch {
        return { recordings: [], drafts: 0, avgScore: null };
    }
};

function useAccountStats({ isAuthenticated, getAccessTokenSilently }) {
    const [token, setToken] = useState(null);
    const [serverDrafts, setServerDrafts] = useState(null);
    const [serverAvgScore, setServerAvgScore] = useState(null);
    const [localStats, setLocalStats] = useState(readLocalStats);

    useEffect(() => {
        if (!isAuthenticated) {
            setToken(null);
            setServerDrafts(null);
            setServerAvgScore(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);
            } catch {
                if (!cancelled) setToken(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, getAccessTokenSilently]);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "recordings" || e.key === "answers") {
                setLocalStats(readLocalStats());
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    const { items: serverRecordings } = useRecordings({ token });

    useEffect(() => {
        if (!token) return;

        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/me/answers`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error("Drafts fetch failed");
                const rows = await res.json();
                if (!ac.signal.aborted) {
                    setServerDrafts(Array.isArray(rows) ? rows : []);
                }
            } catch (e) {
                if (!ac.signal.aborted) setServerDrafts([]);
            }
        })();

        return () => ac.abort();
    }, [token]);

    useEffect(() => {
        if (!token) return;

        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/me/stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error("Stats fetch failed");
                const data = await res.json();
                if (!ac.signal.aborted) {
                    setServerAvgScore(
                        data?.avg_score == null ? null : Number(data.avg_score)
                    );
                }
            } catch {
                if (!ac.signal.aborted) setServerAvgScore(null);
            }
        })();

        return () => ac.abort();
    }, [token]);

    const recordingsForStats = isAuthenticated
        ? serverRecordings
        : localStats.recordings;
    const draftsForStats = isAuthenticated
        ? serverDrafts?.length ?? 0
        : localStats.drafts;
    return useMemo(
        () => ({
            recordings: recordingsForStats.length,
            drafts: draftsForStats,
            avgScore: isAuthenticated
                ? serverAvgScore
                : computeAvgScore(recordingsForStats),
            token,
        }),
        [
            recordingsForStats,
            draftsForStats,
            serverAvgScore,
            isAuthenticated,
            token,
        ]
    );
}

export default function Home() {
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const didFirstLoginRedirect = useRef(false);
    const stats = useAccountStats({
        isAuthenticated,
        getAccessTokenSilently,
    });
    const {
        profile,
        loading: profileLoading,
        error: profileError,
    } = useApiProfile({ enabled: isAuthenticated, syncOn404: false });
    const needsDashboardTour = profileNeedsDashboardTour(profile);
    const isProAiPlan =
        String(profile?.plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";
    const hasStats =
        Number(stats.recordings) > 0 ||
        Number(stats.drafts) > 0 ||
        stats.avgScore != null;

    useEffect(() => {
        if (!isAuthenticated) return;
        if (didFirstLoginRedirect.current) return;
        if (profileLoading) return;

        const profileNotFound = Number(profileError?.status) === 404;
        const id = profile?.id;

        let hasSeenTour = false;
        if (id) {
            try {
                hasSeenTour =
                    localStorage.getItem(dashboardTourSeenKey(id)) === "1";
            } catch {
                hasSeenTour = false;
            }
        }

        const shouldRedirect =
            profileNotFound || (needsDashboardTour && !hasSeenTour);
        if (!shouldRedirect) return;

        didFirstLoginRedirect.current = true;
        try {
            localStorage.setItem(DASHBOARD_TOUR_PENDING_KEY, "1");
        } catch {
            /* noop */
        }
        navigate("/dashboard", { replace: true });
    }, [
        isAuthenticated,
        needsDashboardTour,
        navigate,
        profile?.id,
        profileError?.status,
        profileLoading,
    ]);
    const showDashboardLoading =
        isAuthenticated && (profileLoading || (!profile && !profileError));

    const howItWorks = [
        {
            title: "Pick a question",
            icon: (
                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <path d="M9 9h6M9 12h6M9 15h4" />
                </svg>
            ),
            content:
                "Choose from real interview prompts tailored to the role you are preparing for.",
        },
        {
            title: "Record your answer",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M3 19l18 0" />
                    <path d="M5 6m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z" />
                </svg>
            ),
            content:
                "Practice on camera with a timer and optional notes, so your delivery feels natural under pressure.",
        },
        {
            title: "Review and improve",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3" />
                    <path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3" />
                </svg>
            ),
            content:
                "Replay your response, spot gaps, and record a tighter, clearer version.",
        },
    ];

    const goToSignup = () => {
        requestPasswordGate({
            type: "auth0Login",
            options: { authorizationParams: { screen_hint: "signup" } },
        });
    };

    const location = useLocation();

    useEffect(() => {
        if (location.pathname !== "/") return;
        if (location.hash !== "#demo") return;

        let tries = 0;
        const maxTries = 20;
        const tick = () => {
            const el = document.getElementById("demo");
            if (el) {
                el.scrollIntoView({ behavior: "auto", block: "start" });
                return;
            }
            tries += 1;
            if (tries < maxTries) window.requestAnimationFrame(tick);
        };
        tick();
    }, [location.pathname, location.hash]);

    if (showDashboardLoading) {
        return <Loading message="Preparing your dashboard…" />;
    }

    return (
        <div className="home" id="home">
            {/* ── HERO ── */}
            <section
                className={`section-1${isAuthenticated ? " section-1--authed" : ""}`}
                id="home"
            >
                <div className="container">
                    <div className="hero-inner">
                        <div className="hero-text">
                            {/* ── LOGGED OUT ── */}
                            {!isAuthenticated ? (
                                <div>
                                    <h1 className="heading">
                                        Get the job you're{" "}
                                        <span className="heading-highlight">
                                            already
                                        </span>{" "}
                                        qualified for
                                    </h1>
                                    <p className="subheading">
                                        Record, review, and reshape your
                                        interview responses so they land exactly
                                        how you intend.
                                    </p>
                                    <div className="hero-actions">
                                        <button
                                            className="btn-signup"
                                            onClick={goToSignup}
                                        >
                                            Start your first session
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line
                                                    x1="5"
                                                    y1="12"
                                                    x2="19"
                                                    y2="12"
                                                />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </button>
                                        {/* <a href="#demo" className="btn-demo">
                                            <span className="btn-demo-play">
                                                <svg
                                                    width="10"
                                                    height="10"
                                                    viewBox="0 0 10 10"
                                                    fill="currentColor"
                                                >
                                                    <polygon points="2,1 9,5 2,9" />
                                                </svg>
                                            </span>
                                            See how it works
                                        </a> */}
                                    </div>
                                    {/* <p className="hero-session-proof">1,200+ sessions this week</p>
                                    <span className="hero-divider" aria-hidden="true" /> */}
                                    <div className="hero-trust">
                                        {[
                                            "No card required",
                                            "Works in-browser",
                                            "Real interview flow",
                                        ].map((t) => (
                                            <span
                                                className="trust-item"
                                                key={t}
                                            >
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                /* ── LOGGED IN ── */
                                <div className="hero-logged-in">
                                    {/* Greeting */}
                                    <div className="hero-logged-in__greeting">
                                        <h1 className="heading">
                                            {needsDashboardTour
                                                ? "Hello"
                                                : "Welcome back"}{" "}
                                            <span className="heading-highlight">
                                                {profile?.first_name ||
                                                    user?.given_name ||
                                                    " "}
                                            </span>
                                        </h1>
                                        <p className="subheading">
                                            {needsDashboardTour
                                                ? ". Start your adventure"
                                                : "Pick up where you left off"}
                                        </p>
                                        {needsDashboardTour ? (
                                            <Link
                                                to="/dashboard"
                                                className="btn-signup"
                                            >
                                                Visit dashboard
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <line
                                                        x1="5"
                                                        y1="12"
                                                        x2="19"
                                                        y2="12"
                                                    />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </Link>
                                        ) : (
                                            ""
                                        )}
                                    </div>

                                    {/* Stat strip */}
                                    {isProAiPlan && hasStats && (
                                        <div
                                            className="hero-stats"
                                            aria-label="Your stats"
                                        >
                                            <div className="hero-stat">
                                                <span className="hero-stat__value">
                                                    {/* {stats.recordings} */}
                                                    {stats.recordings
                                                        ? stats.recordings
                                                        : "—"}
                                                </span>
                                                <span className="hero-stat__label">
                                                    Recordings
                                                </span>
                                            </div>
                                            <div
                                                className="hero-stat__divider"
                                                aria-hidden="true"
                                            />
                                            <div className="hero-stat">
                                                <span className="hero-stat__value">
                                                    {stats.drafts
                                                        ? stats.drafts
                                                        : "—"}
                                                </span>
                                                <span className="hero-stat__label">
                                                    Drafts saved
                                                </span>
                                            </div>
                                            <div
                                                className="hero-stat__divider"
                                                aria-hidden="true"
                                            />
                                            <div className="hero-stat">
                                                <span className="hero-stat__value">
                                                    {stats.avgScore !== null
                                                        ? formatScore(
                                                              stats.avgScore
                                                          )
                                                        : "—"}
                                                </span>
                                                <span className="hero-stat__label">
                                                    Avg score
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── REST OF PAGE — LOGGED OUT ONLY ── */}
            {/* HOW IT WORKS */}
            {!isAuthenticated && (
                <section className="s-how" id="how">
                    <div className="container">
                        <div className="s-how__head">
                            <span className="section-eyebrow">
                                How it works
                            </span>
                            <h2 className="section-h2">
                                Practice like the real thing
                            </h2>
                            <p className="section-sub">
                                A realistic interview flow built for delivery on
                                the day: practise, replay, refine.
                            </p>
                        </div>

                        <div className="steps-grid">
                            {STEPS.map((s) => (
                                <div className="step-card" key={s.n}>
                                    <span className="step-card__num">
                                        {s.n}
                                    </span>
                                    <h3 className="step-card__title">
                                        {s.title}
                                    </h3>
                                    <p className="step-card__body">{s.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* DEMO */}
            {!isAuthenticated && (
                <section className="section-3" id="demo">
                    <div className="container">
                        <div className="demo-wrapper">
                            <div className="section-intro">
                                <span className="section-eyebrow">Demo</span>
                                <h2>See it in action</h2>
                                <p>
                                    No fluff, just the flow of how our practice
                                    sessions work.
                                </p>
                            </div>
                            <div className="video-wrapper">
                                <a
                                    className="video-thumbnail"
                                    href="https://www.example.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Watch the Mooz demo (opens in a new tab)"
                                >
                                    <div
                                        className="video-thumbnail__chrome"
                                        aria-hidden="true"
                                    >
                                        <div className="video-thumbnail__dots">
                                            <span className="video-thumbnail__dot video-thumbnail__dot--close" />
                                            <span className="video-thumbnail__dot video-thumbnail__dot--min" />
                                            <span className="video-thumbnail__dot video-thumbnail__dot--max" />
                                        </div>
                                    </div>
                                    <div className="video-thumbnail__media">
                                        <img
                                            src={require("../assets/demo-thumbnail.png")}
                                            alt="Mooz demo thumbnail"
                                        />
                                        <span
                                            className="btn-play"
                                            aria-hidden="true"
                                        >
                                            <span
                                                className="home-icon home-icon--play"
                                                aria-hidden="true"
                                            />
                                        </span>
                                        <div className="video-overlay" />
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* PRICING */}
            {!isAuthenticated && (
                <section className="section-4" id="pricing">
                    <div className="container">
                        <div className="section-intro">
                            <span className="section-eyebrow">Pricing</span>
                            <h2>Choose your pace</h2>
                            <p>
                                Start free, then upgrade when you want AI
                                feedback and higher practice limits.
                            </p>
                        </div>
                        <div className="plans">
                            <div className="plan-card">
                                <div className="plan-header">
                                    <h3>Basic</h3>
                                    <div className="plan-badge-free">Free</div>
                                </div>
                                <p className="plan-description">
                                    Everything you need to start building
                                    interview confidence.
                                </p>
                                <ul className="plan-list">
                                    {[
                                        "Question bank access",
                                        "Record practice answers",
                                        "Save and replay recordings",
                                        "Basic progress tracking",
                                    ].map((f) => (
                                        <li key={f}>
                                            <svg
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className="btn-plan btn-plan--free"
                                    onClick={goToSignup}
                                >
                                    Get started free
                                </button>
                            </div>

                            <div className="plan-card plan-card--pro">
                                <div className="plan-header">
                                    <h3>Pro AI</h3>
                                    <div className="plan-badge-pro">
                                        Upgrade
                                    </div>
                                </div>
                                <p className="plan-description">
                                    AI-powered coaching to sharpen your
                                    structure and delivery.
                                </p>
                                <ul className="plan-list">
                                    {[
                                        "Everything in Free",
                                        "AI feedback on your recordings",
                                        "Structure & clarity coaching",
                                        "Higher practice limits",
                                    ].map((f) => (
                                        <li key={f}>
                                            <svg
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/pricing"
                                    className="btn-plan btn-plan--pro"
                                    onClick={navigateToTop}
                                >
                                    View pricing
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ */}
            {!isAuthenticated && (
                <section className="section-5" id="faq">
                    <div className="container">
                        <div className="faq-header">
                            <span className="section-eyebrow">FAQ</span>
                            <h2>Common questions</h2>
                            <p>Your questions, answered.</p>
                        </div>
                        <div className="faq-body">
                            <Accordion
                                ids={["3", "13", "14", "15", "16"]}
                                showCategories={false}
                            />
                            <Link
                                to="/faqs"
                                className="btn-faqs"
                                onClick={navigateToTop}
                            >
                                See all FAQs{" "}
                                <span
                                    className="home-icon home-icon--arrow"
                                    aria-hidden="true"
                                />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* BOTTOM CTA */}
            {!isAuthenticated && (
                <section className="s-cta">
                    <div className="container">
                        <div className="cta-inner">
                            <span className="cta-tag">
                                Built for the final stretch
                            </span>
                            <h2 className="cta-heading">
                                Make your next interview your best
                            </h2>
                            <p className="cta-body">
                                Record, replay, and refine your delivery.
                            </p>
                            <button
                                className="btn-feedback"
                                onClick={goToSignup}
                            >
                                Choose your first question
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M3 8h10M9 4l4 4-4 4"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
